'use client'
import { useEffect, useRef } from 'react'

import { useTranslation } from '@/hooks/client'
import { tw } from '@/lib/utils'

import { LauncherPanel } from './launcher-panel'
import { LauncherScrim } from './launcher-scrim'
import styles from './launcher-styles'
import { useLauncherBounds } from './use-launcher-bounds'
import { useLauncherFocus } from './use-launcher-focus'
import { useTypeToSearch } from './use-type-to-search'

import { NavContext } from '../nav-context'
import { useNavContext } from '../nav-context-provider'
import sheetStyles from '../sheet/nav-mobile-sheet-styles'
import { SheetSurfaceContext } from '../sheet/sheet-surface-context'
import { useSheetInert } from '../sheet/use-sheet-inert'
import { useSheetPresence } from '../sheet/use-sheet-presence'
import { useSheetScrollLock } from '../sheet/use-sheet-scroll-lock'
import { useMenuComponent } from '../use-menu-component'
import { useMenuSelection } from '../use-menu-selection'
import { useNavSearchQuery } from '../use-nav-search-query'

const loadResults = () => import('./launcher-results').then((module) => module.LauncherResults)

// The desktop menu: one band for every section and for search, dropped from
// the bar across the whole page over a dimmed page. Its contents sit in the
// bar's own column (LauncherPanel), so the panel tabs start under the logo.
// It renders inside the <nav> so it tracks the bar (sticky, or scrolling away
// on product pages); only the scrim is portalled, because the nav's transform
// would otherwise contain it. Stacking: the scrim (1005) sits above the page's
// floating widgets and site popups (up to 1003), and NavWrapper lifts the nav,
// band included, to 1010 while a menu is open. The dialog is not aria-modal on
// purpose: the bar above it stays operable and switches sections; the page
// behind is inert instead.
export const NavLauncher = () => {
  const navContext = useNavContext()
  const { t, locale } = useTranslation()
  const activeContainer = navContext.current.containerId
    ? navContext.nav.containers.find((container) => container.id === navContext.current.containerId)
    : undefined
  const open = !navContext.isMobile && !!activeContainer
  const state = useSheetPresence(open)
  // Render on the same update as the caret. Presence owns only the animation
  // lifecycle; waiting for its effect leaves the first click without a panel.
  const present = open || state !== 'closed'
  const Results = useMenuComponent(!navContext.isMobile, open, loadResults)

  const current = useMenuSelection(open, navContext.current)
  const container = navContext.nav.containers.find((container) => container.id === current.containerId)
  const isSearch = container?.type === 'search'

  const query = useNavSearchQuery(locale)
  const { reset } = query
  const anchorRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const panelId = current.panelId

  // A query belongs to the band, not to a list: switching sections or panels
  // starts clean. Closing keeps the query visible until the band has exited.
  useEffect(() => {
    reset()
  }, [reset, container?.id, panelId, present])
  useSheetScrollLock(present)
  useSheetInert(present)
  const { maxHeight, top } = useLauncherBounds(anchorRef, present)
  useLauncherFocus({ panelRef, inputRef, state, isSearch: !!isSearch, containerId: container?.id ?? '' })
  const onKeyDown = useTypeToSearch({ inputRef, hasQuery: query.searchTerm.length > 0, clearQuery: reset })
  // The band hugs its content; it holds a floor only while rows are pending
  // (the deferred nav's skeletons, a search in flight) so it cannot collapse
  // and re-grow around a list that is about to land.
  const floor = !Results || navContext.isContentLoading || query.searching

  if (!present || !container) {
    return null
  }

  return (
    <NavContext.Provider value={{ ...navContext, current }}>
      <SheetSurfaceContext.Provider value="desktop-launcher">
        <LauncherScrim state={state} dark={navContext.isDarkNav} top={top} />
        <div ref={anchorRef} className="absolute inset-x-0 top-[80px] z-50">
          <div
            ref={panelRef}
            role="dialog"
            aria-label={isSearch ? t('nav.search', 'Search') : container.title}
            aria-busy={!Results || navContext.isContentLoading}
            tabIndex={-1}
            data-menu-clickaway
            data-nav-launcher
            onKeyDown={onKeyDown}
            className={tw(
              'flex w-full flex-col overflow-hidden bg-(--sh-surface) outline-none',
              sheetStyles.sheet,
              navContext.isDarkNav ? sheetStyles.dark : sheetStyles.cream,
              styles.panel,
              navContext.isDarkNav && styles.panelDark,
              state === 'closing' ? styles.panelLift : styles.panelDrop
            )}
            style={{ maxHeight, minHeight: floor && maxHeight ? `min(420px, ${maxHeight})` : undefined }}
          >
            <LauncherPanel container={container} query={query} inputRef={inputRef} Results={Results} />
          </div>
        </div>
      </SheetSurfaceContext.Provider>
    </NavContext.Provider>
  )
}
